import React, { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaCheck, FaAward, FaCrown, FaStar, FaShieldAlt, FaChevronDown, 
  FaQuestionCircle, FaTimes, FaOm, FaLeaf, FaMedal 
} from "react-icons/fa";
import { 
  FiArrowRight, FiCheckCircle, FiLogIn, FiUserPlus, FiRefreshCw, FiZap, FiShield, FiPhoneCall 
} from "react-icons/fi";
import { GiLotus } from "react-icons/gi";
import { useNavigate } from "react-router-dom";
import { userAPI } from "../services/api";
import { packageService } from "../services/packageService";
import { getAuth } from "../utils/auth";

// Animation Variants
const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.15 }
  }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 35 },
  show: { opacity: 1, y: 0, transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] } }
};

// FAQ Data
const faqItems = [
  {
    question: "Can I transition or switch between batches easily?",
    answer: "Absolutely! We offer complete scheduling flexibility. If your personal routine changes, you can request to switch between the Morning Awakening and Evening Inner Strength batches at any time directly through your dashboard."
  },
  {
    question: "What happens if I miss a live virtual session?",
    answer: "Every single live class is recorded in high definition and uploaded to your secure Member Dashboard. You can catch up at your convenience and access on-demand practice sessions anytime."
  },
  {
    question: "How do dynamic subscription plans work?",
    answer: "All plans are dynamically synchronized with our studio schedule. When you activate a plan, your access to live batches, AI diet planners, and exclusive workshops is immediately activated in real-time."
  },
  {
    question: "Can I cancel or upgrade my tier anytime?",
    answer: "Yes, you can upgrade your plan or change billing frequency at any time from your Member Profile Subscription tab. Upgrades take effect immediately with pro-rated credit for unused days."
  },
  {
    question: "Is there guidance for complete beginners?",
    answer: "Yes! Beginners receive gentle step-by-step modifications, breath alignment cues, and personalized posture guidance from Mrs. Divya Nithin Bhojwani and certified senior instructors."
  }
];

export default function PricingPage() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState("all"); // "all", "monthly", "annual"
  const [openFaq, setOpenFaq] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedPkgForModal, setSelectedPkgForModal] = useState(null);

  // Mouse light tracking for lower section background image
  const lowerSectionRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const handleLowerMouseMove = (e) => {
    if (!lowerSectionRef.current) return;
    const rect = lowerSectionRef.current.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  // Fetch dynamic packages from live backend API
  const fetchLivePackages = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 1. Fetch from public packages endpoint
      let list = [];
      const res = await userAPI.getPublicPackages().catch(() => null);
      if (res?.data?.data && Array.isArray(res.data.data)) {
        list = res.data.data;
      } else if (res?.data && Array.isArray(res.data)) {
        list = res.data;
      }
      
      // 2. Fallback to package service
      if (!list || list.length === 0) {
        const fallback = await packageService.fetchPackages().catch(() => null);
        list = fallback?.packages || [];
      }

      // Filter only active packages
      const active = (list || []).filter(p => p.isActive !== false);

      // Sort by accessLevel ascending, then price ascending
      active.sort((a, b) => {
        const levelA = a.accessLevel ?? 1;
        const levelB = b.accessLevel ?? 1;
        if (levelA !== levelB) return levelA - levelB;
        return (a.price || 0) - (b.price || 0);
      });

      setPackages(active);
    } catch (err) {
      console.error("Failed to load packages:", err);
      setError("Unable to load latest subscription packages. Please try refreshing.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLivePackages();

    // Listen to real-time updates dispatched when admin modifies packages
    const handleUpdate = () => {
      fetchLivePackages();
    };

    window.addEventListener("packages-updated", handleUpdate);
    window.addEventListener("subscription-updated", handleUpdate);
    return () => {
      window.removeEventListener("packages-updated", handleUpdate);
      window.removeEventListener("subscription-updated", handleUpdate);
    };
  }, []);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  // Helper to extract strictly REAL features entered in DB
  const getPackageFeatures = (pkg) => {
    const list = [];
    
    if (pkg.featuresJson) {
      try {
        const parsed = typeof pkg.featuresJson === "string" ? JSON.parse(pkg.featuresJson) : pkg.featuresJson;
        if (Array.isArray(parsed)) {
          parsed.forEach(f => {
            if (f && typeof f === "string" && f.trim().length > 0) {
              list.push(f.trim());
            }
          });
        } else if (typeof parsed === "string" && parsed.trim().length > 0) {
          list.push(parsed.trim());
        }
      } catch {
        if (typeof pkg.featuresJson === "string") {
          pkg.featuresJson.split(",").forEach(f => {
            if (f && f.trim().length > 0) list.push(f.trim());
          });
        }
      }
    }

    return list;
  };

  // Helper to format duration display directly from durationInDays
  const formatDuration = (pkg) => {
    const days = pkg.durationInDays || 0;
    if (days === 0) return "Lifetime";
    if (days === 15) return "15 Days Trial";
    if (days === 30) return "1 Month";
    if (days === 60) return "2 Months";
    if (days === 90) return "3 Months";
    if (days === 180) return "6 Months";
    if (days === 200) return "200 Days";
    if (days >= 360) return "1 Year / Annual";
    return `${days} Days`;
  };

  // Filter packages based on billing toggle
  const filteredPackages = useMemo(() => {
    if (billingPeriod === "all") return packages;
    if (billingPeriod === "monthly") {
      return packages.filter(p => (p.durationInDays || 0) <= 60 && (p.durationInDays || 0) > 0);
    }
    if (billingPeriod === "annual") {
      return packages.filter(p => (p.durationInDays || 0) >= 90 || (p.durationInDays || 0) === 0);
    }
    return packages;
  }, [packages, billingPeriod]);

  // Handle user clicking "Subscribe / Purchase"
  const handlePurchase = (pkg) => {
    const auth = getAuth();
    const targetUrl = `/user/profile?tab=subscription&packageId=${pkg.id}`;

    if (auth.token) {
      // Logged in: navigate directly to Profile subscription checkout tab
      navigate(targetUrl);
    } else {
      // Unauthenticated: save package in session and prompt auth modal
      sessionStorage.setItem("pending_subscription_package_id", String(pkg.id));
      sessionStorage.setItem("auth_redirect", targetUrl);
      setSelectedPkgForModal(pkg);
      setShowAuthModal(true);
    }
  };

  return (
    <div 
      className="min-h-screen text-[#111111] overflow-x-hidden relative"
      style={{
        background: "linear-gradient(180deg, #FFF8EE 0%, #FFF5E6 15%, #FAF7ED 40%, #FFF7EC 70%, #FFF8EE 100%)"
      }}
    >
      
      {/* ══════════════════════════════════════════════════════════════════════════
          1. HERO SECTION WITH OLIVE GREEN, ORANGE & WARM BROWN OVERLAP (NO WHITE SHADOW)
      ══════════════════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen pt-32 pb-24 lg:pt-36 lg:pb-28 flex items-center justify-center overflow-hidden">
        
        {/* Background Photorealistic Temple Ashram */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/assets/pricing_hero_bg.jpg" 
            alt="Sacred Yogic Sanctuary" 
            className="w-full h-full object-cover scale-[1.03]"
            style={{ filter: "brightness(0.55) saturate(1.2)" }}
          />
          
          {/* Overlapping Rich Amber, Saffron, Warm Espresso & Olive Green Tints */}
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              background: "linear-gradient(135deg, rgba(20, 8, 2, 0.65) 0%, rgba(242, 154, 12, 0.32) 38%, rgba(35, 14, 2, 0.58) 70%, rgba(55, 68, 44, 0.75) 100%)",
            }}
          />

          {/* Ambient Warm Golden Sun Burst & Saffron Glow */}
          <div 
            className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[850px] h-[580px] pointer-events-none rounded-full"
            style={{
              background: "radial-gradient(circle, rgba(242, 154, 12, 0.42) 0%, rgba(255, 180, 0, 0.2) 45%, transparent 72%)",
              filter: "blur(60px)",
            }}
          />

          {/* ── HERO BOTTOM TRANSITION: OLIVE GREEN, SAFFRON ORANGE & WARM BROWN OVERLAP (NO WHITE SHADOW) ── */}
          <div 
            className="absolute bottom-0 left-0 right-0 h-52 pointer-events-none"
            style={{
              background: "linear-gradient(to bottom, transparent 0%, rgba(32, 14, 4, 0.45) 30%, rgba(242, 154, 12, 0.35) 60%, rgba(68, 88, 54, 0.85) 88%, #FFF8EE 100%)"
            }}
          />

          {/* Radiant Accent Seam: Olive Green & Orange Edge Glow */}
          <div 
            className="absolute bottom-0 inset-x-0 h-[2.5px] pointer-events-none opacity-85"
            style={{
              background: "linear-gradient(90deg, transparent 0%, #4D6638 20%, #F29A0C 50%, #688A54 80%, transparent 100%)"
            }}
          />
        </div>

        {/* Floating Sacred Golden & Olive Aura Particles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
          {[...Array(24)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                y: [0, -50, 0],
                x: [0, ((i * 7) % 30) - 15, 0],
                opacity: [0.3, 0.95, 0.3],
                scale: [1, 1.4, 1],
              }}
              transition={{
                duration: 4.5 + (i % 5) * 0.8,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.25,
              }}
              className="absolute rounded-full"
              style={{
                width: `${3.5 + (i % 4) * 2}px`,
                height: `${3.5 + (i % 4) * 2}px`,
                backgroundColor: i % 3 === 0 ? "#FFD166" : i % 3 === 1 ? "#F29A0C" : "#9DC08B",
                boxShadow: "0 0 12px rgba(242, 154, 12, 0.75)",
                top: `${10 + (i * 7.5) % 75}%`,
                left: `${5 + (i * 8.5) % 90}%`,
              }}
            />
          ))}
        </div>

        {/* Hero Central Header */}
        <div className="relative z-10 max-w-[1160px] mx-auto px-6 text-center space-y-7">
          
          {/* Top Royal Seal Badge */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-3 px-6 py-2.5 rounded-full bg-black/55 border border-[#F29A0C]/50 backdrop-blur-2xl shadow-[0_12px_35px_rgba(0,0,0,0.5)]"
          >
            <GiLotus className="text-[#FFD166] text-base animate-pulse" />
            <span className="tracking-[3px] uppercase text-[11px] font-bold text-white">
              DIVYYOG SACRED MEMBERSHIPS
            </span>
            <span className="w-1.5 h-1.5 rounded-full bg-[#F29A0C]" />
            <span className="text-[11px] font-semibold text-[#FFD166]">
              Verified Studio Access
            </span>
          </motion.div>

          {/* Main Hero Headline */}
          <div className="relative">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-[700px] h-[200px] bg-black/55 rounded-full blur-[45px] pointer-events-none" />

            <motion.h1 
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="relative text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-black leading-[1.12] tracking-tight text-white"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              <span className="block drop-shadow-[0_4px_25px_rgba(0,0,0,0.95)]">
                Invest In Your Sacred
              </span>
              <span 
                className="block mt-1 sm:mt-2 bg-gradient-to-r from-[#FFF2B2] via-[#FFD166] to-[#F29A0C] bg-clip-text text-transparent"
                style={{
                  filter: "drop-shadow(0 4px 20px rgba(0,0,0,0.95)) drop-shadow(0 0 35px rgba(242,154,12,0.65))"
                }}
              >
                Transformation
              </span>
            </motion.h1>
          </div>

          {/* Subtitle */}
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.28 }}
            className="text-base sm:text-lg md:text-xl text-white/90 font-medium leading-[1.75] max-w-3xl mx-auto drop-shadow-[0_2px_12px_rgba(0,0,0,0.85)]"
          >
            Select a pathway built for your personal schedule and wellness objectives. All subscriptions connect dynamically with live sessions, recorded masterclasses, and certified mentor guidance.
          </motion.p>

          {/* Dynamic Billing Filter Switch */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.94 }} 
            animate={{ opacity: 1, scale: 1 }} 
            transition={{ duration: 0.6, delay: 0.42 }}
            className="inline-flex items-center gap-2 bg-[#1A0C04]/80 backdrop-blur-2xl border border-[#F29A0C]/40 rounded-full p-2 shadow-[0_20px_50px_rgba(0,0,0,0.5)]"
          >
            <button 
              onClick={() => setBillingPeriod("all")}
              className={`px-6 py-2.5 rounded-full text-xs font-bold tracking-wider uppercase transition-all duration-300 outline-none cursor-pointer ${
                billingPeriod === "all" 
                  ? "bg-gradient-to-r from-[#F29A0C] to-[#E08700] text-white shadow-[0_6px_20px_rgba(242,154,12,0.55)] scale-[1.03]" 
                  : "text-white/70 hover:text-white"
              }`}
            >
              All Packages ({packages.length})
            </button>
            <button 
              onClick={() => setBillingPeriod("monthly")}
              className={`px-6 py-2.5 rounded-full text-xs font-bold tracking-wider uppercase transition-all duration-300 outline-none cursor-pointer ${
                billingPeriod === "monthly" 
                  ? "bg-gradient-to-r from-[#F29A0C] to-[#E08700] text-white shadow-[0_6px_20px_rgba(242,154,12,0.55)] scale-[1.03]" 
                  : "text-white/70 hover:text-white"
              }`}
            >
              Monthly / Regular
            </button>
            <button 
              onClick={() => setBillingPeriod("annual")}
              className={`relative px-6 py-2.5 rounded-full text-xs font-bold tracking-wider uppercase transition-all duration-300 outline-none cursor-pointer ${
                billingPeriod === "annual" 
                  ? "bg-gradient-to-r from-[#6B8A5A] to-[#8CAE78] text-white shadow-[0_6px_20px_rgba(107,138,90,0.55)] scale-[1.03]" 
                  : "text-white/70 hover:text-white"
              }`}
            >
              Long Term & Annual
              <span className="absolute -top-3 right-1 bg-[#F29A0C] text-white text-[8px] font-black tracking-normal px-2 py-0.5 rounded-full shadow-md animate-pulse">
                BEST VALUE
              </span>
            </button>
          </motion.div>

        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════════
          2. LIGHT GRADIENT ROYAL THEME: DYNAMIC CARDS (100% REAL API DATA)
      ══════════════════════════════════════════════════════════════════════════ */}
      <div className="relative z-20 max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 mt-6 mb-24">
        
        {/* Real-time sync badge */}
        <div className="flex items-center justify-center gap-2.5 text-xs text-[#8C5819] font-bold uppercase tracking-widest mb-12">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
          <span>Live Synchronized Packages from Studio</span>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="py-24 text-center">
            <div className="inline-block w-12 h-12 border-4 border-[#F29A0C] border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-zinc-600 font-semibold text-sm">Retrieving dynamic packages from database...</p>
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className="p-8 rounded-[28px] bg-red-50 border border-red-200 text-center max-w-lg mx-auto mb-16 shadow-sm">
            <p className="text-red-700 font-bold mb-4">{error}</p>
            <button 
              onClick={fetchLivePackages}
              className="px-6 py-2.5 rounded-full bg-[#1F1F1F] text-white text-xs font-bold uppercase tracking-wider hover:bg-black transition-all cursor-pointer inline-flex items-center gap-2 border-none"
            >
              <FiRefreshCw size={13} /> Try Again
            </button>
          </div>
        )}

        {/* Empty Packages State */}
        {!loading && !error && filteredPackages.length === 0 && (
          <div className="p-12 rounded-[32px] bg-white border border-[#EEDCC7] text-center max-w-lg mx-auto mb-16 shadow-sm">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-amber-100 flex items-center justify-center text-amber-700 mb-4">
              <FaAward size={22} />
            </div>
            <h3 className="text-xl font-bold text-zinc-900 mb-2">No Packages In This Category</h3>
            <p className="text-zinc-600 text-sm mb-6">Switch your filter to view all available tiers.</p>
            <button 
              onClick={() => setBillingPeriod("all")}
              className="px-6 py-2.5 rounded-full bg-[#F29A0C] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#D98700] transition-all cursor-pointer border-none"
            >
              Show All Packages
            </button>
          </div>
        )}

        {/* Dynamic Cards Grid (Light Royal Theme + Enhanced Opacity Icons & Animations) */}
        {!loading && !error && filteredPackages.length > 0 && (
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.05 }}
            className={`grid gap-8 items-stretch ${
              filteredPackages.length === 1 
                ? "grid-cols-1 max-w-md mx-auto" 
                : filteredPackages.length === 2 
                ? "grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto" 
                : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
            }`}
          >
            {filteredPackages.map((pkg, idx) => {
              const features = getPackageFeatures(pkg);
              const durationLabel = formatDuration(pkg);
              const originalPrice = pkg.price || 0;
              const discountPct = pkg.discountPercentage || 0;
              const finalPrice = pkg.discountedPrice !== undefined && pkg.discountedPrice !== null 
                ? pkg.discountedPrice 
                : (originalPrice * (1 - discountPct / 100));

              const isFree = originalPrice === 0 || (pkg.packageType || "").toLowerCase().includes("free");
              
              // Highlighting top tier
              const isPopular = pkg.badgeTag?.toLowerCase().includes("popular") || 
                                (pkg.packageType || "").toLowerCase().includes("premium") || 
                                idx === 1;

              return (
                <motion.div 
                  key={pkg.id || idx} 
                  variants={fadeInUp} 
                  whileHover={{ y: -10, transition: { duration: 0.32, ease: "easeOut" } }}
                  className="flex flex-col h-full group"
                >
                  <div 
                    className="relative overflow-hidden rounded-[38px] p-8 sm:p-9 flex flex-col justify-between h-full transition-all duration-500 cursor-pointer group-hover:shadow-[0_30px_75px_rgba(242,154,12,0.20),0_10px_30px_rgba(78,98,64,0.10)]"
                    style={{
                      background: isPopular
                        ? "linear-gradient(150deg, #FFFFFF 0%, #FFF8EE 45%, #F7FAF3 100%)"
                        : "linear-gradient(150deg, #FFFFFF 0%, #FCF9F3 55%, #F4F7EF 100%)",
                      border: isPopular
                        ? "2.5px solid #F29A0C"
                        : "1.5px solid #F4D9B5",
                      boxShadow: isPopular
                        ? "0 25px 70px rgba(242, 154, 12, 0.18), 0 4px 20px rgba(0,0,0,0.04)"
                        : "0 20px 50px rgba(0, 0, 0, 0.05), 0 4px 15px rgba(242, 154, 12, 0.04)",
                    }}
                  >
                    {/* Watermark Lotus Icon: HIGH OPACITY & ATTRACTIVE HOVER ANIMATION */}
                    <div 
                      className="absolute -top-4 -right-4 text-[#F29A0C] pointer-events-none text-[130px] transition-all duration-700 ease-out transform group-hover:scale-115 group-hover:rotate-12"
                      style={{
                        opacity: isPopular ? 0.24 : 0.18,
                        filter: "drop-shadow(0 4px 12px rgba(242, 154, 12, 0.25))"
                      }}
                    >
                      <GiLotus />
                    </div>

                    <div>
                      
                      {/* Top Badges & Tier Header */}
                      <div className="flex items-center justify-between gap-2 mb-6">
                        <span 
                          className="text-[10px] tracking-[2px] uppercase font-black px-4 py-1.5 rounded-full shadow-sm transition-transform duration-300 group-hover:scale-105"
                          style={{
                            background: isPopular
                              ? "linear-gradient(135deg, #F29A0C, #FFA726)"
                              : "rgba(107, 138, 90, 0.15)",
                            color: isPopular ? "#FFFFFF" : "#3F5F2C",
                            border: isPopular ? "none" : "1px solid rgba(107, 138, 90, 0.35)"
                          }}
                        >
                          {pkg.badgeTag || pkg.packageType || "YOGA TIER"}
                        </span>

                        <div 
                          className="w-11 h-11 rounded-2xl flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 shadow-sm"
                          style={{
                            background: isPopular 
                              ? "linear-gradient(135deg, #FFF4E3, #FFE8C7)" 
                              : "linear-gradient(135deg, #FFF8EE, #FFF0DC)",
                            border: "1.5px solid #F4D9B5",
                            color: "#F29A0C",
                            boxShadow: "0 4px 14px rgba(242, 154, 12, 0.18)"
                          }}
                        >
                          {isPopular ? <FaCrown size={16} className="animate-pulse" /> : <FaAward size={16} />}
                        </div>
                      </div>

                      {/* Package Name directly from DB */}
                      <h3 
                        className="text-2xl sm:text-[26px] font-black tracking-tight text-[#1A0C04] mb-2 transition-colors duration-300 group-hover:text-[#B86B00]"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        {pkg.name}
                      </h3>

                      {/* Package Description directly from DB (no static text) */}
                      {pkg.description ? (
                        <p className="text-xs sm:text-sm text-zinc-600 leading-[22px] mb-6 min-h-[44px] font-medium">
                          {pkg.description}
                        </p>
                      ) : (
                        <div className="mb-6 min-h-[44px]" />
                      )}

                      {/* Pricing Box directly from DB */}
                      <div className="mb-6 p-4 rounded-2xl bg-[#FFF8EE] border border-[#F4D9B5]/80 transition-all duration-300 group-hover:border-[#F29A0C]/60 group-hover:bg-[#FFF5E6]">
                        <div className="flex items-baseline gap-2">
                          <h4 
                            className="text-[40px] sm:text-[46px] leading-none font-black text-[#1A0C04]"
                            style={{ fontFamily: "'Poppins', sans-serif" }}
                          >
                            {isFree ? "Free" : `₹${Math.round(finalPrice).toLocaleString()}`}
                          </h4>
                          
                          <span className="text-xs font-semibold text-zinc-500">
                            / {durationLabel}
                          </span>
                        </div>

                        {/* Strikethrough original price if discounted */}
                        {!isFree && discountPct > 0 && (
                          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-[#F4D9B5]/60">
                            <span className="text-xs line-through text-zinc-400 font-semibold">
                              ₹{Math.round(originalPrice).toLocaleString()}
                            </span>
                            <span className="text-[10px] font-black text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full border border-emerald-300">
                              SAVE {discountPct}%
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Divider */}
                      <div className="h-[1px] w-full my-5 bg-[#F4D9B5]/70" />

                      {/* Dynamic Features from DB */}
                      <div className="mb-6">
                        {features.length > 0 ? (
                          <>
                            <p className="text-[10px] uppercase font-bold tracking-[1.5px] text-[#8C5819] mb-3">
                              Package Features ({features.length})
                            </p>
                            <ul className="space-y-3">
                              {features.map((feat, fIdx) => (
                                <li key={fIdx} className="flex items-start gap-3">
                                  <div 
                                    className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 transition-transform duration-300 group-hover:scale-110"
                                    style={{
                                      background: "rgba(242, 154, 12, 0.16)",
                                      border: "1px solid rgba(242, 154, 12, 0.45)",
                                      color: "#D97706"
                                    }}
                                  >
                                    <FaCheck size={8} />
                                  </div>
                                  <span className="text-xs sm:text-[13px] font-semibold text-zinc-800 leading-relaxed">
                                    {feat}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </>
                        ) : null}

                        {/* Allowed Capabilities from DB */}
                        {Array.isArray(pkg.allowedFeatures) && pkg.allowedFeatures.length > 0 && (
                          <div className="mt-4 flex flex-wrap gap-1.5">
                            {pkg.allowedFeatures.map((feat, aIdx) => (
                              <span 
                                key={aIdx}
                                className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-[#6B8A5A]/12 text-[#3A5528] border border-[#6B8A5A]/30 transition-colors duration-300 hover:bg-[#6B8A5A]/20"
                              >
                                {feat.replace(/_/g, " ")}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Real DB limits */}
                      <div className="flex flex-wrap gap-2 mb-8">
                        <span className="text-[10px] font-bold px-3 py-1 rounded-lg bg-zinc-100 text-zinc-700 border border-zinc-200">
                          Classes: {pkg.maxClassesPerDay || 1}/day
                        </span>
                        <span className="text-[10px] font-bold px-3 py-1 rounded-lg bg-zinc-100 text-zinc-700 border border-zinc-200">
                          AI Diet Plans: {pkg.aiDietGenerationLimit || 5}/mo
                        </span>
                      </div>
                    </div>

                    {/* Action Button with Hover Glow & Sliding Arrow */}
                    <button 
                      onClick={() => handlePurchase(pkg)}
                      className="w-full py-4 rounded-full font-bold text-xs tracking-wider uppercase transition-all duration-300 shadow-md cursor-pointer outline-none flex items-center justify-center gap-2 border-none text-white group-hover:scale-[1.02] active:scale-[0.98]"
                      style={{
                        background: isPopular
                          ? "linear-gradient(135deg, #F29A0C 0%, #FFA726 50%, #E08700 100%)"
                          : "linear-gradient(135deg, #1A0C04 0%, #301708 100%)",
                        boxShadow: isPopular
                          ? "0 8px 25px rgba(242,154,12,0.42)"
                          : "0 8px 20px rgba(26,12,4,0.3)"
                      }}
                    >
                      <span>{isFree ? "Start Free Trial" : "Begin Transformation"}</span>
                      <FiArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                    </button>

                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════════
          3. THREE TRUST CARDS SECTION (BACKGROUND IMAGE & INTERACTIVE EFFECTS ONLY HERE)
      ══════════════════════════════════════════════════════════════════════════ */}
      <section 
        ref={lowerSectionRef}
        onMouseMove={handleLowerMouseMove}
        className="relative py-24 px-5 sm:px-8 lg:px-14 overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgba(18, 8, 3, 0.96) 0%, rgba(32, 14, 5, 0.93) 50%, rgba(14, 5, 2, 0.97) 100%)",
        }}
      >
        {/* Background Sacred Mandala Artwork — ONLY Behind These 3 Cards */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-80 mix-blend-screen scale-[1.04] pointer-events-none transition-transform duration-1000"
          style={{ backgroundImage: "url('/assets/trainers_bottom_bg.jpg')" }}
        />

        {/* Deep Amber & Espresso Vignette */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/40 to-black/85 pointer-events-none" />

        {/* Dynamic Interactive Mouse Light Spotlight */}
        <div
          className="absolute pointer-events-none rounded-full blur-[80px] transition-opacity duration-300 opacity-85"
          style={{
            width: "520px",
            height: "520px",
            background: "radial-gradient(circle, rgba(242,154,12,0.45) 0%, rgba(255,209,102,0.2) 45%, transparent 70%)",
            left: `${mousePos.x - 260}px`,
            top: `${mousePos.y - 260}px`,
          }}
        />

        {/* Rotating Sacred Mandala Watermark in Corner */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 75, repeat: Infinity, ease: "linear" }}
          className="absolute -right-24 -bottom-24 w-[420px] h-[420px] opacity-15 text-[#FFD166] pointer-events-none"
        >
          <FaOm className="w-full h-full" />
        </motion.div>

        {/* Animated Golden Glowing Border Shimmer at Top Seam */}
        <div
          className="absolute top-0 left-0 right-0 h-[3px]"
          style={{
            background: "linear-gradient(90deg, transparent, #4D6638, #F29A0C, #FFD166, #F29A0C, #4D6638, transparent)",
          }}
        />

        {/* Bottom Seam Accent */}
        <div
          className="absolute bottom-0 left-0 right-0 h-[3px]"
          style={{
            background: "linear-gradient(90deg, transparent, #F29A0C, #4D6638, #FFD166, #4D6638, #F29A0C, transparent)",
          }}
        />

        <div className="relative z-10 max-w-[1280px] mx-auto">
          
          {/* SECTION HEADER */}
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-3">
            <span className="text-[11px] uppercase tracking-[3px] font-black text-[#FFD166] bg-black/55 border border-[#F29A0C]/40 px-5 py-2 rounded-full inline-flex items-center gap-2 shadow-lg">
              <FaLeaf className="text-[#9DC08B]" />
              UNCOMPROMISING SACRED STANDARDS
            </span>
            <h2 
              className="text-3xl sm:text-5xl font-black text-white tracking-tight"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Why Practitioners Choose Divyyog
            </h2>
            <p className="text-white/80 text-sm sm:text-base font-medium">
              Every membership is anchored in traditional Himalayan heritage, certified pedagogy, and live personal attention.
            </p>
          </div>

          {/* 3 TRUST PILLARS (ROYAL GLASSMORPHIC CARDS) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            
            <motion.div 
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              className="p-8 sm:p-9 rounded-[34px] bg-black/50 backdrop-blur-2xl border border-white/20 hover:border-[#F29A0C] hover:bg-black/65 transition-all duration-500 flex flex-col items-center text-center shadow-2xl group cursor-pointer"
            >
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center text-[#FFD166] mb-6 shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:rotate-6"
                style={{
                  background: "linear-gradient(135deg, rgba(242, 154, 12, 0.28), rgba(242, 154, 12, 0.08))",
                  border: "1.5px solid rgba(242, 154, 12, 0.55)",
                  boxShadow: "0 8px 24px rgba(242, 154, 12, 0.22)"
                }}
              >
                <FaShieldAlt size={24} />
              </div>
              <h4 className="text-xl sm:text-[22px] font-bold text-white mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Secure & Instant Activation
              </h4>
              <p className="text-white/80 text-xs sm:text-sm leading-relaxed font-normal">
                Encrypted end-to-end payment processing directly synchronized with your profile ledger and class schedule.
              </p>
            </motion.div>

            <motion.div 
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              className="p-8 sm:p-9 rounded-[34px] bg-black/50 backdrop-blur-2xl border border-white/20 hover:border-[#9DC08B] hover:bg-black/65 transition-all duration-500 flex flex-col items-center text-center shadow-2xl group cursor-pointer"
            >
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center text-[#9DC08B] mb-6 shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:rotate-6"
                style={{
                  background: "linear-gradient(135deg, rgba(107, 138, 90, 0.3), rgba(107, 138, 90, 0.08))",
                  border: "1.5px solid rgba(107, 138, 90, 0.55)",
                  boxShadow: "0 8px 24px rgba(107, 138, 90, 0.22)"
                }}
              >
                <FaAward size={24} />
              </div>
              <h4 className="text-xl sm:text-[22px] font-bold text-white mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Gold Medalist Pedagogy
              </h4>
              <p className="text-white/80 text-xs sm:text-sm leading-relaxed font-normal">
                Curriculum overseen by Mrs. Divya Nithin Bhojwani, international Gold Medalist and certified Yoga Alliance master teachers.
              </p>
            </motion.div>

            <motion.div 
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              className="p-8 sm:p-9 rounded-[34px] bg-black/50 backdrop-blur-2xl border border-white/20 hover:border-[#F29A0C] hover:bg-black/65 transition-all duration-500 flex flex-col items-center text-center shadow-2xl group cursor-pointer"
            >
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center text-[#FFD166] mb-6 shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:rotate-6"
                style={{
                  background: "linear-gradient(135deg, rgba(242, 154, 12, 0.28), rgba(242, 154, 12, 0.08))",
                  border: "1.5px solid rgba(242, 154, 12, 0.55)",
                  boxShadow: "0 8px 24px rgba(242, 154, 12, 0.22)"
                }}
              >
                <FiCheckCircle size={26} />
              </div>
              <h4 className="text-xl sm:text-[22px] font-bold text-white mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Interactive Google Meet Batches
              </h4>
              <p className="text-white/80 text-xs sm:text-sm leading-relaxed font-normal">
                Two-way high definition video guidance with real-time posture correction, breath cues, and Q&A after every session.
              </p>
            </motion.div>

          </div>

        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════════
          4. FREQUENTLY ASKED QUESTIONS SECTION (GRADIENT BACKGROUND COLOR WITH EFFECTS)
      ══════════════════════════════════════════════════════════════════════════ */}
      <section 
        className="relative py-28 px-5 sm:px-8 lg:px-14 overflow-hidden"
        style={{
          background: "linear-gradient(180deg, #FFF8EE 0%, #FFF3DF 28%, #F7FAF2 65%, #FFF8EE 100%)"
        }}
      >
        {/* Ambient Glowing Gradient Aura Orbs (Effects) */}
        <motion.div
          animate={{ scale: [1, 1.15, 1], opacity: [0.35, 0.55, 0.35] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-12 left-10 w-[550px] h-[550px] rounded-full pointer-events-none blur-[90px]"
          style={{
            background: "radial-gradient(circle, rgba(242, 154, 12, 0.22) 0%, rgba(255, 209, 102, 0.12) 40%, transparent 70%)"
          }}
        />

        <motion.div
          animate={{ scale: [1, 1.18, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 11, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
          className="absolute bottom-16 right-10 w-[580px] h-[580px] rounded-full pointer-events-none blur-[100px]"
          style={{
            background: "radial-gradient(circle, rgba(107, 138, 90, 0.20) 0%, rgba(157, 192, 139, 0.10) 45%, transparent 70%)"
          }}
        />

        {/* Sacred Shimmer Particles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
          {[...Array(16)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                y: [0, -40, 0],
                opacity: [0.25, 0.85, 0.25],
                scale: [1, 1.3, 1],
              }}
              transition={{
                duration: 4.8 + (i % 4) * 0.9,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.35,
              }}
              className="absolute rounded-full"
              style={{
                width: `${3 + (i % 3) * 2}px`,
                height: `${3 + (i % 3) * 2}px`,
                backgroundColor: i % 2 === 0 ? "#F29A0C" : "#6B8A5A",
                boxShadow: "0 0 10px rgba(242, 154, 12, 0.5)",
                top: `${12 + (i * 8) % 78}%`,
                left: `${8 + (i * 9) % 84}%`,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 max-w-[1080px] mx-auto">
          
          {/* FAQ HEADER */}
          <div className="text-center mb-14 space-y-3">
            <span className="text-[10px] tracking-[3px] uppercase font-black text-[#8C5819] bg-[#F29A0C]/15 border border-[#F29A0C]/35 px-5 py-2 rounded-full inline-block shadow-sm">
              SADHANA FAQ
            </span>
            <h3 
              className="text-3xl sm:text-5xl font-black text-[#1A0C04] tracking-tight" 
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Frequently Asked Questions
            </h3>
            <p className="text-zinc-600 text-sm sm:text-base font-medium max-w-xl mx-auto">
              Everything you need to know about plans, classroom schedules, and member access.
            </p>
          </div>

          {/* FAQ ACCORDION CARDS */}
          <div className="space-y-4 mb-20">
            {faqItems.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div 
                  key={idx} 
                  className="rounded-[26px] border border-[#F4D9B5] bg-white/90 backdrop-blur-xl shadow-sm overflow-hidden transition-all duration-300 hover:border-[#F29A0C]/60 hover:shadow-md"
                >
                  <button 
                    onClick={() => toggleFaq(idx)}
                    className="w-full px-7 py-5 flex items-center justify-between text-left font-bold text-[#1A0C04] text-base sm:text-lg focus:outline-none transition-colors duration-300 hover:text-[#D97706] cursor-pointer border-none bg-transparent"
                  >
                    <span className="flex items-center gap-3.5 pr-4">
                      <FaQuestionCircle className="text-[#F29A0C] shrink-0" size={16} />
                      {faq.question}
                    </span>
                    <FaChevronDown 
                      size={12} 
                      className={`text-zinc-500 shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180 text-[#F29A0C]" : ""}`} 
                    />
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: "auto" }}
                        exit={{ height: 0 }}
                        transition={{ duration: 0.35, ease: [0.25, 1, 0.5, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="px-7 pb-6 pt-1 text-zinc-700 text-sm leading-[26px] font-medium pl-14 border-t border-[#F4D9B5]/50 bg-[#FFFDFB]">
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          {/* ROYAL DIRECT COUNSELOR ASSISTANCE BOX */}
          <div className="p-8 sm:p-10 rounded-[36px] bg-gradient-to-r from-[#1A0C04] via-[#2E1508] to-[#1A0C04] border border-[#F29A0C]/40 backdrop-blur-2xl shadow-2xl flex flex-col md:flex-row items-center justify-between gap-6 text-white">
            <div className="space-y-2 text-center md:text-left">
              <span className="text-[10px] tracking-[2.5px] uppercase font-bold text-[#FFD166]">
                NEED PERSONAL GUIDANCE?
              </span>
              <h4 className="text-2xl sm:text-3xl font-black text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Unsure which tier fits your journey?
              </h4>
              <p className="text-white/75 text-xs sm:text-sm max-w-xl font-normal">
                Speak directly with our senior sadhana counselors to evaluate your schedule, health profile, and yoga goals.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4 shrink-0">
              <a 
                href="/contact"
                className="px-8 py-4 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FFA726] text-white font-bold text-xs uppercase tracking-wider shadow-lg hover:scale-105 transition-transform no-underline inline-flex items-center gap-2.5"
              >
                <FiPhoneCall size={14} />
                <span>Talk to Counselor</span>
              </a>
            </div>
          </div>

        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════════
          4. AUTHENTICATION REQUIRED MODAL (ROYAL LIGHT SILK GLASS)
      ══════════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showAuthModal && selectedPkgForModal && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[250] flex items-center justify-center p-4"
            style={{ background: "rgba(26, 12, 4, 0.65)", backdropFilter: "blur(10px)" }}
            onClick={() => setShowAuthModal(false)}
          >
            <motion.div 
              initial={{ scale: 0.94, opacity: 0, y: 25 }} 
              animate={{ scale: 1, opacity: 1, y: 0 }} 
              exit={{ scale: 0.94, opacity: 0, y: 25 }}
              className="relative overflow-hidden rounded-[36px] p-8 max-w-[480px] w-full bg-[#FFFDF8] border border-[#F4D9B5] shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              {/* Close Button */}
              <button 
                onClick={() => setShowAuthModal(false)} 
                className="absolute top-6 right-6 w-9 h-9 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center text-zinc-600 transition-colors border-none cursor-pointer"
              >
                <FaTimes size={14} />
              </button>

              <div className="text-center pt-2">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center text-white mx-auto mb-4 shadow-lg"
                  style={{
                    background: "linear-gradient(135deg, #F29A0C, #FFA726)"
                  }}
                >
                  <FaCrown size={24} />
                </div>

                <span className="text-[10px] uppercase tracking-[3px] font-black text-[#D97706]">
                  MEMBERSHIP ACTIVATION
                </span>
                
                <h3 className="text-2xl sm:text-3xl font-black text-[#1A0C04] mt-1 mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Join Divyyog Sadhana
                </h3>

                <p className="text-xs sm:text-sm text-zinc-600 leading-relaxed mb-6">
                  You selected <strong className="text-[#1A0C04] font-extrabold">{selectedPkgForModal.name}</strong>. Please sign in or register to link your account and activate this plan.
                </p>

                {/* Summary Box */}
                <div className="rounded-2xl p-4 bg-[#FFF8EE] border border-[#F4D9B5] mb-6 text-left flex justify-between items-center">
                  <div>
                    <p className="text-[10px] font-bold text-[#D97706] uppercase tracking-wider">Plan</p>
                    <p className="text-sm font-black text-[#1A0C04]">{selectedPkgForModal.name}</p>
                    <p className="text-[11px] text-zinc-500">{formatDuration(selectedPkgForModal)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-base font-black text-[#1A0C04]">
                      ₹{Math.round(selectedPkgForModal.discountedPrice || selectedPkgForModal.price).toLocaleString()}
                    </p>
                    <span className="text-[10px] text-emerald-700 font-bold bg-emerald-100 px-2 py-0.5 rounded-full">
                      Live Activation
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-3">
                  <button 
                    onClick={() => {
                      setShowAuthModal(false);
                      navigate(`/login?redirect=/user/profile?tab=subscription&packageId=${selectedPkgForModal.id}`);
                    }}
                    className="w-full py-4 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8A00] text-white font-bold text-xs tracking-wider uppercase shadow-md hover:scale-[1.01] transition-transform cursor-pointer flex items-center justify-center gap-2 border-none"
                  >
                    <FiLogIn size={15} /> Sign In to Activate
                  </button>

                  <button 
                    onClick={() => {
                      setShowAuthModal(false);
                      navigate(`/register?redirect=/user/profile?tab=subscription&packageId=${selectedPkgForModal.id}`);
                    }}
                    className="w-full py-4 rounded-full bg-white text-zinc-800 border border-[#F4D9B5] font-bold text-xs tracking-wider uppercase hover:bg-zinc-50 transition-all cursor-pointer flex items-center justify-center gap-2"
                  >
                    <FiUserPlus size={15} /> Create New Account
                  </button>
                </div>

                <p className="text-[11px] text-zinc-500 mt-4">
                  Once signed in, you will be taken directly to your profile subscription checkout tab.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
